import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnInit, Output, Input, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DialogBaseComponent } from '@/app/core/components';
import { OrganizationSelectorComponent } from '../entity-selection/organization-selector.component';
import { OrganizationResponseDto } from '@qc/shared/dto';
import { ContactWithRole } from '../../interfaces/contact.interface';
import { Button } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { CalendarModule } from 'primeng/calendar';
import { CheckboxModule } from 'primeng/checkbox';
import { ProductTestingRequirementsResponseDto } from '@/app/core/services/product-testing-requirements.service';
import { OrganizationService } from '@/app/core/services/organization.service';
import { parseDateOnly } from '@/app/shared/utils';

export interface TestingRequirementData {
    id?: string;
    testMethod: string;
    testDescription?: string;
    testSpecifications?: string;
    testRevision?: string;
    isIdentity?: boolean;
    isConfirmatory?: boolean;
    isRelease?: boolean;
    isFull?: boolean;
    testValidation?: string;
    testValidationApprovalDate?: Date;
    testingOrganizationId?: string;
    testingOrganizationDraft?: {
        organization: Partial<OrganizationResponseDto>;
        contacts: ContactWithRole[];
    };
    comments?: string;
}

@Component({
    selector: 'qc-add-testing-requirement-dialog',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        Button,
        InputTextModule,
        TextareaModule,
        CalendarModule,
        CheckboxModule,
        OrganizationSelectorComponent,
    ],
    template: `
        <div class="w-[600px] max-w-[90vw] bg-white rounded-lg shadow-2xl" style="overflow: visible;">
            <div class="flex justify-between items-center p-6 border-b border-gray-200">
                <h2 class="text-xl font-semibold text-gray-900">
                    {{ isEditMode ? 'Edit Testing Requirement' : 'Testing Requirement' }}
                </h2>
                <button type="button" class="p-2 hover:bg-gray-100 rounded-lg" (click)="close()">
                    <i class="pi pi-times text-gray-400"></i>
                </button>
            </div>

            <div class="p-6">
                <form [formGroup]="testingRequirementForm" class="space-y-4">
                    <div>
                        <label class="block mb-2">Test Method *</label>
                        <input
                            pInputText
                            formControlName="testMethod"
                            placeholder="e.g. WSM#4"
                            class="w-full"
                        />
                    </div>

                    <div>
                        <label class="block mb-2">Test Description</label>
                        <textarea
                            pTextarea
                            formControlName="testDescription"
                            placeholder="e.g. Appearance"
                            rows="2"
                            class="w-full"
                        ></textarea>
                    </div>

                    <div>
                        <label class="block mb-2">Test Specifications</label>
                        <textarea
                            pTextarea
                            formControlName="testSpecifications"
                            placeholder="e.g. As per finished product specification"
                            rows="2"
                            class="w-full"
                        ></textarea>
                    </div>

                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <label class="block mb-2">Test Revision</label>
                            <input
                                pInputText
                                formControlName="testRevision"
                                placeholder="e.g. Rev A"
                                class="w-full"
                            />
                        </div>
                        <div>
                            <label class="block mb-2">Test Validation</label>
                            <input
                                pInputText
                                formControlName="testValidation"
                                placeholder="e.g. Validated"
                                class="w-full"
                            />
                        </div>
                    </div>

                    <div class="grid grid-cols-2 gap-4">
                        <label class="flex items-center gap-2">
                            <p-checkbox formControlName="isRelease" [binary]="true" />
                            <span>Release</span>
                        </label>
                        <label class="flex items-center gap-2">
                            <p-checkbox formControlName="isFull" [binary]="true" />
                            <span>Full</span>
                        </label>
                        <label class="flex items-center gap-2">
                            <p-checkbox formControlName="isConfirmatory" [binary]="true" />
                            <span>Confirmatory</span>
                        </label>
                        <label class="flex items-center gap-2">
                            <p-checkbox formControlName="isIdentity" [binary]="true" />
                            <span>Identity</span>
                        </label>
                    </div>

                    <div>
                        <label class="block mb-2">Test Validation Approval Date</label>
                        <p-calendar
                            formControlName="testValidationApprovalDate"
                            dateFormat="yy-mm-dd"
                            [showIcon]="true"
                            styleClass="w-full"
                        />
                    </div>

                    <div>
                        <label class="block mb-2">Testing Organization</label>
                        <qc-organization-selector
                            label=""
                            [required]="false"
                            [useTaggedDisplay]="true"
                            [showCreateButton]="false"
                            [deferSave]="deferSave"
                            [initialValue]="selectedTestingOrganization || null"
                            (organizationSelected)="onTestingOrganizationSelected($event)"
                            (organizationCleared)="onTestingOrganizationCleared()"
                            (organizationCreatedWithContacts)="onTestingOrganizationCreatedWithContacts($event)"
                        />
                    </div>

                    <div>
                        <label class="block mb-2">Comments</label>
                        <textarea
                            pTextarea
                            formControlName="comments"
                            placeholder="Additional comments"
                            rows="2"
                            class="w-full"
                        ></textarea>
                    </div>
                </form>
            </div>

            <div class="flex justify-end gap-2 p-6 border-t border-gray-200">
                <p-button label="Cancel" severity="secondary" variant="outlined" (onClick)="close()" />
                <p-button
                    label="Save"
                    severity="primary"
                    (onClick)="save()"
                    [disabled]="!testingRequirementForm.valid"
                />
            </div>
        </div>
    `,
    styles: [
        `
            :host ::ng-deep .p-calendar {
                z-index: 9999;
            }
            :host ::ng-deep .p-dropdown-panel {
                z-index: 9999;
            }
            :host ::ng-deep .p-calendar-panel {
                position: absolute !important;
                z-index: 10000 !important;
                top: auto !important;
                left: auto !important;
            }
            :host ::ng-deep .p-calendar-panel {
                position: absolute !important;
                z-index: 10000 !important;
                top: auto !important;
                left: auto !important;
            }
        `,
    ],
})
export class AddTestingRequirementDialogComponent extends DialogBaseComponent implements OnInit {
    @Input() initialData: ProductTestingRequirementsResponseDto | null = null;
    @Input() deferSave: boolean = false;
    @Output() testingRequirementAdded = new EventEmitter<TestingRequirementData>();

    private fb = inject(FormBuilder);
    private organizationService = inject(OrganizationService);

    testingRequirementForm!: FormGroup;
    selectedTestingOrganization: OrganizationResponseDto | null = null;
    testingOrganizationDraft: { organization: Partial<OrganizationResponseDto>; contacts: ContactWithRole[] } | null = null;

    get isEditMode(): boolean {
        return !!this.initialData;
    }

    constructor() {
        super();
    }

    ngOnInit(): void {
        this.initForm();
        if (this.initialData) {
            this.loadInitialData();
        }
    }

    private initForm(): void {
        this.testingRequirementForm = this.fb.group({
            testMethod: ['', [Validators.required]],
            testDescription: [''],
            testSpecifications: [''],
            testRevision: [''],
            isIdentity: [false],
            isConfirmatory: [false],
            isRelease: [false],
            isFull: [false],
            testValidation: [''],
            testValidationApprovalDate: [''],
            testingOrganizationId: [''],
            comments: [''],
        });
    }

    onTestingOrganizationSelected(organization: OrganizationResponseDto): void {
        this.selectedTestingOrganization = organization;
        this.testingOrganizationDraft = null;
        this.testingRequirementForm.get('testingOrganizationId')?.setValue(organization.id);
    }

    onTestingOrganizationCreatedWithContacts(data: { organization: OrganizationResponseDto; contacts: ContactWithRole[] }): void {
        if (!this.deferSave) {
            this.onTestingOrganizationSelected(data.organization);
            return;
        }

        const tempOrgId = 'temp-org-' + Date.now();
        this.selectedTestingOrganization = {
            ...data.organization,
            id: (data.organization as any).id || tempOrgId,
        } as OrganizationResponseDto;

        this.testingOrganizationDraft = {
            organization: data.organization,
            contacts: data.contacts || [],
        };

        this.testingRequirementForm.get('testingOrganizationId')?.setValue(this.selectedTestingOrganization.id);
    }

    onTestingOrganizationCleared(): void {
        this.selectedTestingOrganization = null;
        this.testingOrganizationDraft = null;
        this.testingRequirementForm.get('testingOrganizationId')?.setValue(null);
    }

    private loadInitialData(): void {
        if (this.initialData) {
            this.testingRequirementForm.patchValue({
                testMethod: this.initialData.testMethod,
                testDescription: this.initialData.testDescription,
                testSpecifications: this.initialData.testSpecifications,
                testRevision: this.initialData.testRevision,
                isIdentity: (this.initialData as any).isIdentity ?? false,
                isConfirmatory: (this.initialData as any).isConfirmatory ?? false,
                isRelease: (this.initialData as any).isRelease ?? false,
                isFull: (this.initialData as any).isFull ?? false,
                testValidation: this.initialData.testValidation,
                testValidationApprovalDate: parseDateOnly(this.initialData.testValidationApprovalDate),
                testingOrganizationId: this.initialData.testingOrganizationId,
                comments: this.initialData.comments,
            });

            if (this.initialData.testingOrganizationId) {
                this.organizationService.getOrganization(this.initialData.testingOrganizationId).subscribe({
                    next: (org) => {
                        this.selectedTestingOrganization = org as any;
                    },
                    error: () => {
                    },
                });
            }
        }
    }

    save(): void {
        if (this.testingRequirementForm.valid) {
            const formValue = this.testingRequirementForm.value;
            const data: TestingRequirementData = {
                id: this.initialData?.id,
                testMethod: formValue.testMethod,
                testDescription: formValue.testDescription,
                testSpecifications: formValue.testSpecifications,
                testRevision: formValue.testRevision,
                isIdentity: formValue.isIdentity,
                isConfirmatory: formValue.isConfirmatory,
                isRelease: formValue.isRelease,
                isFull: formValue.isFull,
                testValidation: formValue.testValidation,
                testValidationApprovalDate: formValue.testValidationApprovalDate,
                testingOrganizationId: formValue.testingOrganizationId,
                testingOrganizationDraft: this.testingOrganizationDraft || undefined,
                comments: formValue.comments,
            };

            this.testingRequirementAdded.emit(data);
            this.close(true);
        }
    }
}
